import { z } from "zod";

/**
 * Environment Variables Schema
 *
 * Validates and parses all required environment variables at startup.
 * SECURITY: No default localhost values to prevent accidental production misconfigurations.
 */
const envSchema = z.object({
	/**
	 * API base URL for backend requests.
	 * Must be explicitly set - no defaults for security.
	 */
	VITE_API_URL: z.string().url({
		message: "VITE_API_URL must be a valid URL (e.g., https://api.example.com)",
	}),
});

/**
 * Parse raw environment variables from import.meta.env
 */
function parseEnv() {
	const rawEnv = {
		VITE_API_URL: import.meta.env.VITE_API_URL,
	};

	const result = envSchema.safeParse(rawEnv);

	if (!result.success) {
		const errors = result.error.format();
		console.error("❌ Invalid environment variables:", errors);

		// In development, show a helpful error message
		if (import.meta.env.DEV) {
			throw new Error(
				`Missing or invalid environment variables:\n${result.error.issues.map((issue) => `  - ${issue.path.join(".")}: ${issue.message}`).join("\n")}\n\nPlease check your .env file.`,
			);
		}

		// In production, fail fast with a generic message
		throw new Error("Application configuration error. Please contact support.");
	}

	return result.data;
}

// Parse environment variables once at startup
const parsedEnv = parseEnv();

/**
 * Type-safe environment variables object.
 * Use this throughout the application instead of accessing import.meta.env directly.
 */
export const env = {
	/** API base URL for backend requests */
	API_URL: parsedEnv.VITE_API_URL,

	/** Whether we're in development mode */
	DEV: import.meta.env.DEV,

	/** Whether we're in production mode */
	PROD: import.meta.env.PROD,

	/** Current mode (development, production, etc.) */
	MODE: import.meta.env.MODE as "development" | "production" | "test",
} as const;

// Type-safe way to access env vars
type Env = typeof env;
